import React, { useCallback, useState } from 'react';
import type { TaskItemView } from '../../api/generated/models/TaskItemView';
import { TASK_BOARD_STATUSES } from '../../utils/taskHelpers';
import type { TaskBoardStatus } from '../../utils/taskHelpers';
import TaskColumn from './TaskColumn';

interface TaskBoardProps {
  items: TaskItemView[];
  onTaskClick: (taskId: string) => void;
  onTaskStatusChange: (taskId: string, status: string) => void;
  /** False for subusers without mobile_write - cards cannot be dragged. */
  canDrag?: boolean;
}

const TaskBoard: React.FC<TaskBoardProps> = ({
  items,
  onTaskClick,
  onTaskStatusChange,
  canDrag = true,
}) => {
  const [draggingTaskId, setDraggingTaskId] = useState<string | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<TaskBoardStatus | null>(null);

  const handleDragStart = useCallback((taskId: string) => {
    setDraggingTaskId(taskId);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingTaskId(null);
    setDragOverStatus(null);
  }, []);

  const handleDragEnter = useCallback((status: TaskBoardStatus) => {
    setDragOverStatus(status);
  }, []);

  const handleDragLeave = useCallback((status: TaskBoardStatus) => {
    setDragOverStatus((current) => (current === status ? null : current));
  }, []);

  const handleDrop = useCallback((status: TaskBoardStatus, taskId: string) => {
    if (!taskId) {
      setDragOverStatus(null);
      return;
    }

    const task = items.find((item) => item.id === taskId);
    if (task && task.status !== status) {
      onTaskStatusChange(taskId, status);
    }

    setDraggingTaskId(null);
    setDragOverStatus(null);
  }, [items, onTaskStatusChange]);

  return (
    <div className="tasks-board">
      {TASK_BOARD_STATUSES.map((status) => (
        <TaskColumn
          key={status}
          status={status}
          tasks={items.filter((item) => item.status === status)}
          onTaskClick={onTaskClick}
          canDrag={canDrag}
          draggingTaskId={draggingTaskId}
          isDragOver={dragOverStatus === status}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        />
      ))}
    </div>
  );
};

export default TaskBoard;
